/**
 * Exact statement reconciliation over the same prepared rows
 * {@link emitBeancount} would export.
 *
 * Opening and closing are **reader-entered** signed balances — this module
 * never infers an opening from the CSV. Net movement uses
 * {@link preparedSourceContributionText} / {@link preparedAmountText}, never
 * a second parse of raw CSV cells and never a floating-point epsilon.
 *
 * ## Semantics
 *
 * - **Equation.** `opening + net(selected source postings) = expectedClosing`.
 *   The signed difference is `expectedClosing − enteredClosing` (zero →
 *   matched).
 * - **Excluded rows.** Pass already-selected rows, or use
 *   {@link selectRowsForReconciliation} with `excludeRowIndexes`. Excluded
 *   indexes never contribute movement or errors.
 * - **Posting-aware movement.** A row contributes the sum of the postings
 *   it actually emits to the source account (`projectCsvRowPostings`). When
 *   `counterAccount === sourceAccount`, both emitted postings hit the source
 *   and cancel; the contribution is zero at the row's decimals — summing the
 *   CSV amount alone would disagree with the ledger. A split row's contra
 *   postings never name the source, so `-100.00` split 60/40 still moves the
 *   statement by `-100.00`.
 * - **Liability signs.** Balances are signed as Beancount stores them (a
 *   credit-card liability of 500 owed is typically `-500`). Refunds,
 *   debit/credit columns and `invertAmounts` are already baked into each
 *   {@link PreparedCsvRow}'s signed amount / emitted text.
 * - **Zero balances.** An explicit `0` / `0.00` opening or closing is valid
 *   and distinct from a missing field (missing → incomplete).
 * - **Precision.** Arithmetic is scaled integers at
 *   `max(opening, closing, selected rows)` decimal places, after each value
 *   is taken through the same `toFixed` path the emitter uses. A float
 *   `PreparedCsvRow.amount` that would print as `"1.01"` contributes `1.01`,
 *   not an unrounded intermediate.
 * - **Currency.** One statement currency; every selected row must match it.
 *   Mixed or foreign rows are unsupported (no FX).
 * - **Dates.** Statement start/end must parse and satisfy `start ≤ end`.
 *   Every selected row's date must fall inside that inclusive window.
 * - **Empty selection.** Zero selected (exportable) rows cannot match —
 *   that is invalid, not a zero-movement success.
 */

import { isValidAccount, isValidCommodity } from "@/lib/beancount-syntax";
import { toCsvExcludeSet } from "@/lib/csv-exclude-set";
import {
  formatScaled,
  isZeroAmountText,
  negateAmountText,
} from "@/lib/fixed-point-text";
import {
  type DateOrder,
  normalizeDate,
  parseAmount,
} from "@/lib/import-normalize";
import {
  type PreparedCsvRow,
  type PrepareCsvRowResult,
  preparedSourceContributionText,
} from "@/lib/csv-to-beancount";

/** Equity account used for an explicit opening transaction in complete mode. */
export const OPENING_BALANCES_ACCOUNT = "Equity:Opening-Balances";

/** Distinct reconciliation outcomes for UI / emission gating. */
export type StatementReconciliationStatus =
  | "disabled"
  | "incomplete"
  | "invalid"
  | "unsupported"
  | "matched"
  | "mismatch";

/** Machine-readable reason when status is not matched/mismatch, or for mismatch context. */
export type StatementReconciliationReason =
  | "not_enabled"
  | "missing_fields"
  | "empty_selection"
  | "invalid_dates"
  | "date_order"
  | "row_outside_window"
  | "invalid_opening"
  | "invalid_closing"
  | "invalid_currency"
  | "mixed_currency"
  | "row_errors"
  | "non_finite"
  | "invalid_source"
  | "balance_mismatch";

export interface StatementReconciliationResult {
  status: StatementReconciliationStatus;
  reason?: StatementReconciliationReason;
  /** ISO statement start when dates parsed. */
  startDate?: string;
  /** ISO statement end when dates parsed. */
  endDate?: string;
  currency?: string;
  /** Signed opening as exact decimal text at the working scale. */
  openingText?: string;
  /** Net selected source movement at the working scale. */
  netMovementText?: string;
  /** opening + net, at the working scale. */
  expectedClosingText?: string;
  /** Reader-entered closing at the working scale. */
  enteredClosingText?: string;
  /**
   * `expectedClosing − enteredClosing` at the working scale.
   * Present for matched (always `"0.00"`-style zero) and mismatch.
   */
  differenceText?: string;
  /** How many prepared rows contributed (excludes excluded + errored). */
  selectedCount: number;
  /** Working decimal places used for scaled arithmetic. */
  scale?: number;
  /** Non-excluded prepare failures that blocked reconciliation. */
  rowErrors?: readonly { rowIndex: number; line: number; reason: string }[];
}

interface ReconcileStatementInput {
  /** When false, returns `disabled` without inspecting balances. */
  enabled: boolean;
  startDateText: string;
  endDateText: string;
  /** Signed opening balance text (empty → incomplete when enabled). */
  openingText: string;
  /** Signed closing balance text (empty → incomplete when enabled). */
  closingText: string;
  currency: string;
  sourceAccount: string;
  /** Date order for non-ISO statement date fields. */
  dateOrder?: DateOrder;
  prepareResults: readonly PrepareCsvRowResult[];
  excludeRowIndexes?: ReadonlySet<number> | readonly number[];
}

interface SelectedReconciliationRows {
  rows: PreparedCsvRow[];
  rowErrors: { rowIndex: number; line: number; reason: string }[];
  /** How many prepare results were dropped by `excludeRowIndexes`. */
  excluded: number;
}

/**
 * Filter prepare results the way {@link emitBeancount} does: excluded indexes
 * are omitted entirely (errors on them suppressed); remaining failures surface
 * as `rowErrors`. Emit and reconcile must share this partition so status and
 * output cannot diverge on selection.
 */
export function selectRowsForReconciliation(
  prepareResults: readonly PrepareCsvRowResult[],
  excludeRowIndexes?: ReadonlySet<number> | readonly number[],
): SelectedReconciliationRows {
  const exclude = toCsvExcludeSet(excludeRowIndexes);
  const rows: PreparedCsvRow[] = [];
  const rowErrors: SelectedReconciliationRows["rowErrors"] = [];
  let excluded = 0;

  for (const prepared of prepareResults) {
    const index = prepared.ok ? prepared.row.rowIndex : prepared.rowIndex;
    if (exclude.has(index)) {
      excluded += 1;
      continue;
    }
    if (!prepared.ok) {
      rowErrors.push({
        rowIndex: prepared.rowIndex,
        line: prepared.line,
        reason: prepared.reason,
      });
      continue;
    }
    rows.push(prepared.row);
  }

  return { rows, rowErrors, excluded };
}

/**
 * Reconcile an explicit statement against the selected exportable rows.
 */
export function reconcileStatement(
  input: ReconcileStatementInput,
): StatementReconciliationResult {
  if (!input.enabled) {
    return { status: "disabled", reason: "not_enabled", selectedCount: 0 };
  }
  const selected = selectRowsForReconciliation(
    input.prepareResults,
    input.excludeRowIndexes,
  );
  return reconcileStatementFromSelection({
    enabled: true,
    startDateText: input.startDateText,
    endDateText: input.endDateText,
    openingText: input.openingText,
    closingText: input.closingText,
    currency: input.currency,
    sourceAccount: input.sourceAccount,
    dateOrder: input.dateOrder,
    rows: selected.rows,
    rowErrors: selected.rowErrors,
  });
}

/**
 * Same as {@link reconcileStatement}, but consumes the partition
 * {@link emitBeancount} already built — one selection feeds status and output.
 */
export function reconcileStatementFromSelection(
  input: {
    enabled: boolean;
    startDateText: string;
    endDateText: string;
    openingText: string;
    closingText: string;
    currency: string;
    sourceAccount: string;
    dateOrder?: DateOrder;
    rows: readonly PreparedCsvRow[];
    rowErrors: SelectedReconciliationRows["rowErrors"];
  },
): StatementReconciliationResult {
  if (!input.enabled) {
    return { status: "disabled", reason: "not_enabled", selectedCount: 0 };
  }

  const startRaw = input.startDateText.trim();
  const endRaw = input.endDateText.trim();
  const openingRaw = input.openingText.trim();
  const closingRaw = input.closingText.trim();
  const currencyRaw = input.currency.trim();

  if (!startRaw || !endRaw || !openingRaw || !closingRaw || !currencyRaw) {
    return { status: "incomplete", reason: "missing_fields", selectedCount: 0 };
  }

  if (!isValidCommodity(currencyRaw)) {
    return {
      status: "invalid",
      reason: "invalid_currency",
      selectedCount: 0,
    };
  }

  // Matched must imply emittable: reject equity-as-source and invalid
  // accounts before arithmetic so status and download copy never disagree.
  const sourceAccount = input.sourceAccount.trim();
  if (
    !isValidAccount(sourceAccount) ||
    sourceAccount === OPENING_BALANCES_ACCOUNT
  ) {
    return {
      status: "invalid",
      reason: "invalid_source",
      selectedCount: 0,
    };
  }

  const order = input.dateOrder ?? "mdy";
  const startParsed = normalizeDate(startRaw, order);
  const endParsed = normalizeDate(endRaw, order);
  if ("error" in startParsed || "error" in endParsed) {
    return { status: "invalid", reason: "invalid_dates", selectedCount: 0 };
  }
  if (startParsed.date > endParsed.date) {
    return {
      status: "invalid",
      reason: "date_order",
      startDate: startParsed.date,
      endDate: endParsed.date,
      selectedCount: 0,
    };
  }

  const opening = parseBalanceExact(openingRaw);
  if (!opening) {
    return { status: "invalid", reason: "invalid_opening", selectedCount: 0 };
  }
  const closing = parseBalanceExact(closingRaw);
  if (!closing) {
    return { status: "invalid", reason: "invalid_closing", selectedCount: 0 };
  }

  const { rows, rowErrors } = input;
  const startDate = startParsed.date;
  const endDate = endParsed.date;
  const currency = currencyRaw;

  if (rowErrors.length > 0) {
    return {
      status: "invalid",
      reason: "row_errors",
      startDate,
      endDate,
      currency,
      selectedCount: rows.length,
      rowErrors,
    };
  }

  if (rows.length === 0) {
    return {
      status: "invalid",
      reason: "empty_selection",
      startDate,
      endDate,
      currency,
      selectedCount: 0,
    };
  }

  for (const row of rows) {
    if (row.date < startDate || row.date > endDate) {
      return {
        status: "invalid",
        reason: "row_outside_window",
        startDate,
        endDate,
        currency,
        selectedCount: rows.length,
      };
    }
    if (row.currency !== currency) {
      return {
        status: "unsupported",
        reason: "mixed_currency",
        startDate,
        endDate,
        currency,
        selectedCount: rows.length,
      };
    }
  }

  return reconcileSelectedRows({
    startDate,
    endDate,
    currency,
    sourceAccount,
    opening,
    closing,
    rows,
  });
}

interface ExactDecimal {
  /** Digit string from the authoritative `toFixed` text (no float residual). */
  text: string;
  decimals: number;
}

function reconcileSelectedRows(args: {
  startDate: string;
  endDate: string;
  currency: string;
  sourceAccount: string;
  opening: ExactDecimal;
  closing: ExactDecimal;
  rows: readonly PreparedCsvRow[];
}): StatementReconciliationResult {
  const { startDate, endDate, currency, sourceAccount, opening, closing, rows } =
    args;

  const contributionTexts: string[] = [];
  let scale = Math.max(opening.decimals, closing.decimals);
  for (const row of rows) {
    const text = preparedSourceContributionText(row, sourceAccount);
    contributionTexts.push(text);
    scale = Math.max(scale, fractionDigits(text), row.decimals);
  }

  const openingScaled = scaleAmountText(opening.text, scale);
  const closingScaled = scaleAmountText(closing.text, scale);
  if (openingScaled === null || closingScaled === null) {
    return {
      status: "invalid",
      reason: "non_finite",
      startDate,
      endDate,
      currency,
      selectedCount: rows.length,
    };
  }

  let net = 0n;
  for (const text of contributionTexts) {
    const part = scaleAmountText(text, scale);
    if (part === null) {
      return {
        status: "invalid",
        reason: "non_finite",
        startDate,
        endDate,
        currency,
        selectedCount: rows.length,
      };
    }
    net += part;
  }

  const expected = openingScaled + net;
  const difference = expected - closingScaled;
  const shared = {
    startDate,
    endDate,
    currency,
    openingText: formatScaled(openingScaled, scale),
    netMovementText: formatScaled(net, scale),
    expectedClosingText: formatScaled(expected, scale),
    enteredClosingText: formatScaled(closingScaled, scale),
    differenceText: formatScaled(difference, scale),
    selectedCount: rows.length,
    scale,
  };

  if (difference === 0n) {
    return { status: "matched", ...shared };
  }

  return {
    status: "mismatch",
    reason: "balance_mismatch",
    ...shared,
  };
}

/**
 * Parse a reader-entered balance through {@link parseAmount}, then take the
 * same `toFixed(decimals)` fingerprint the emitter uses so reconciliation
 * never compares an unrounded float to emitted text.
 */
function parseBalanceExact(raw: string): ExactDecimal | null {
  const parsed = parseAmount(raw);
  if (parsed === null || !Number.isFinite(parsed.value)) return null;
  const text = parsed.value.toFixed(parsed.decimals);
  if (!FIXED_AMOUNT_RE.test(text)) return null;
  return { text, decimals: parsed.decimals };
}

const FIXED_AMOUNT_RE = /^-?\d+\.\d+$/;

function fractionDigits(fixedText: string): number {
  const dot = fixedText.indexOf(".");
  return dot === -1 ? 0 : fixedText.length - dot - 1;
}

/**
 * Convert authoritative fixed-point text to a scaled integer at `scale`
 * decimals. Extends with trailing zeros when `scale` exceeds the text's own
 * fraction; refuses a longer fraction than `scale` (should not happen when
 * scale is a max over inputs).
 */
function scaleAmountText(fixedText: string, scale: number): bigint | null {
  const m = /^(-?)(\d+)\.(\d+)$/.exec(fixedText.trim());
  if (!m) return null;
  const neg = m[1] === "-";
  const intPart = m[2];
  let frac = m[3];
  if (frac.length > scale) return null;
  frac = frac.padEnd(scale, "0");
  // "-0.00" and "0.00" both yield 0n.
  const digits = BigInt(intPart + frac);
  return neg ? -digits : digits;
}

/**
 * Add `deltaDays` to a `YYYY-MM-DD` calendar date using a UTC-noon anchor so
 * month/year/leap boundaries stay on the civil calendar (no DST shift).
 */
function shiftCalendarDay(isoDate: string, deltaDays: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    throw new Error(`Invalid calendar date: ${isoDate}`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  utc.setUTCDate(utc.getUTCDate() + deltaDays);
  return utc.toISOString().slice(0, 10);
}

/** Calendar day after `isoDate` — the closing assertion date. */
export function nextCalendarDay(isoDate: string): string {
  return shiftCalendarDay(isoDate, 1);
}

/** Calendar day before `isoDate` — the explicit opening transaction date. */
export function previousCalendarDay(isoDate: string): string {
  return shiftCalendarDay(isoDate, -1);
}

/**
 * Directives a matched statement may contribute to the converter artifact.
 *
 * Append mode never includes an opening transaction — it depends on the
 * existing ledger already carrying the opening balance. Complete mode emits
 * an opening transaction only for a nonzero opening (zero skips a redundant
 * empty transfer).
 */
interface StatementEmissionParts {
  /** Day before statement start when an opening transaction is emitted. */
  openingDate?: string;
  /** Full opening transaction block (no trailing newline). */
  openingTransaction?: string;
  /** Calendar day after statement end (Beancount asserts at start of day). */
  assertionDate: string;
  /** Full `YYYY-MM-DD balance Account Amount CUR` line. */
  closingAssertion: string;
  /** Whether complete mode must `open` {@link OPENING_BALANCES_ACCOUNT}. */
  needsEquityOpen: boolean;
}

/**
 * Build opening/closing directive strings for a **matched** result only.
 * Mismatch, invalid, incomplete, unsupported and disabled return `null` so
 * the converter keeps an ordinary artifact with no statement directives and
 * never inserts a balancing plug.
 */
export function planMatchedStatementEmission(args: {
  result: StatementReconciliationResult;
  sourceAccount: string;
  outputMode: "complete" | "append";
  /** Pad width already computed for transaction postings (account + 2). */
  accountWidth: number;
}): StatementEmissionParts | null {
  const { result, outputMode, accountWidth } = args;
  if (result.status !== "matched") return null;
  if (
    !result.startDate ||
    !result.endDate ||
    !result.currency ||
    result.openingText === undefined ||
    result.enteredClosingText === undefined
  ) {
    return null;
  }

  const source = args.sourceAccount.trim();
  if (
    !isValidAccount(source) ||
    source === OPENING_BALANCES_ACCOUNT ||
    !isValidCommodity(result.currency)
  ) {
    return null;
  }

  const assertionDate = nextCalendarDay(result.endDate);
  const parts: StatementEmissionParts = {
    assertionDate,
    closingAssertion: `${assertionDate} balance ${source} ${result.enteredClosingText} ${result.currency}`,
    needsEquityOpen: false,
  };

  // Append never inserts an opening transaction — the existing ledger must
  // already hold the statement opening. Complete mode skips a zero opening
  // rather than emitting a no-op or invalid transfer.
  if (outputMode === "complete" && !isZeroAmountText(result.openingText)) {
    const openingDate = previousCalendarDay(result.startDate);
    const width = Math.max(
      accountWidth,
      OPENING_BALANCES_ACCOUNT.length + 2,
      source.length + 2,
    );
    const equityAmount = negateAmountText(result.openingText);
    parts.openingDate = openingDate;
    parts.needsEquityOpen = true;
    parts.openingTransaction = [
      `${openingDate} * "Opening balance"`,
      `  ${source.padEnd(width)}${result.openingText} ${result.currency}`,
      `  ${OPENING_BALANCES_ACCOUNT.padEnd(width)}${equityAmount} ${result.currency}`,
    ].join("\n");
  }

  return parts;
}
